//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import moment from "moment-timezone";
import { Card, Col, Container, Row, Button, Table } from "react-bootstrap";
import { useTable, useSortBy, usePagination } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Context } from "../common/context/Context";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { SystemMessageActions } from "./messageAction";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";

const SystemMessageTable = ({ systemMessageCMSData, columns, data }) => {
  const navigate = useNavigate();
  const context = useContext(Context);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    useSortBy,
    usePagination,
  );

  const rowClick = (row) => {
    if (
      context?.permissionsList?.includes("System Messages.editsystemmessages")
    ) {
      navigate("/systemMessage/edit", { state: row.original });
    }
  };

  const fetchSystemMessageTableLabel = (key) => {
    if (systemMessageCMSData && systemMessageCMSData.Labels) {
      return (
        systemMessageCMSData.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  return (
    <div>
      <h6 className="mt-5">
        {fetchSystemMessageTableLabel("SystemMessage.ActiveSystemMessage")}
      </h6>
      <Table className="mt-3" {...getTableProps()} responsive>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  {(column.render("Header") === "Countries" ||
                    column.render("Header") === "Language" ||
                    column.render("Header") === "From" ||
                    column.render("Header") === "To") && (
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                style={{ cursor: "pointer" }}
                {...row.getRowProps()}
                onClick={() => rowClick(row)}
              >
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const SearchSystemMessage = ({ candidateCMSData }) => {
  const [countriesJson, setCountriesJson] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [systemMessages, setSystemMessages] = useState([]);
  const [SystemMessageJson, setSystemMessageJson] = useState({});
  const [isSearched, setIsSearched] = useState(false);
  const [systemMessageCMSData, setSystemMessageCMSData] = useState(
    candidateCMSData?.pageContent["Candidates.SystemMessage"],
  );
  const [commonComponentCMSData, setCommonComponentCMSData] = useState(
    candidateCMSData?.pageContent["CommonComponent"],
  );
  const [errorAlert, setErrorAlert] = useState({
    message: "",
    showAlert: false,
  });

  const fetchSystemMessageLabel = (key) => {
    if (systemMessageCMSData && systemMessageCMSData.Labels) {
      return (
        systemMessageCMSData.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  const fetchCommonComponentLabel = (key) => {
    if (commonComponentCMSData && commonComponentCMSData.Labels) {
      return (
        commonComponentCMSData.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  const messageTableColumn = [
    {
      id: "SystemMessage.Column.MessageId",
      Header: fetchSystemMessageLabel("SystemMessage.MessageID"),
      accessor: "messageId",
    },
    {
      id: "SystemMessage.Column.Countries",
      Header: fetchSystemMessageLabel("SystemMessage.Countries"),
      accessor: "countryCodes",
    },
    {
      id: "SystemMessage.Column.Language",
      Header: fetchSystemMessageLabel("SystemMessage.Edit.LanguageLabel"),
      accessor: "languageCode",
    },
    {
      id: "SystemMessage.Column.MessageHeader",
      Header: fetchSystemMessageLabel("SystemMessage.messageHeaderLabel"),
      accessor: "messageTitle",
    },
    {
      id: "SystemMessage.Column.Message",
      Header: fetchSystemMessageLabel("SystemMessage.Message"),
      accessor: "messageDescription",
    },
    {
      id: "SystemMessage.Column.From",
      Header: fetchSystemMessageLabel("SystemMessage.From"),
      accessor: (values) => {
        const { fromDate, timezone } = values;
        return moment(fromDate)?.tz(timezone)?.format("YYYY-MM-DD HH:mm");
      },
    },
    {
      id: "SystemMessage.Column.To",
      Header: fetchSystemMessageLabel("SystemMessage.To"),
      accessor: (values) => {
        const { toDate, timezone } = values;
        return moment(toDate)?.tz(timezone)?.format("YYYY-MM-DD HH:mm");
      },
    },
  ];

  const fetchCountriesJson = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await SystemMessageActions.GetCountriesJson().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          const countriesCodeRes = JSON.parse(
            JSON.stringify(res?.data.countryLocaleCodes),
          );
          const countries = [];
          countriesCodeRes.forEach((data) => {
            if (countries.length > 0) {
              let countryexists = countries.filter(function (item) {
                return item.value === data.countryIdentifier;
              });

              if (countryexists.length == 0) {
                countries.push({
                  value: data.countryIdentifier,
                  label: data.countryIdentifier,
                });
              }
            } else {
              countries.push({
                value: data.countryIdentifier,
                label: data.countryIdentifier,
              });
            }
          });

          setCountriesJson(countries);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
        }
      },
      (err) => {
        console.log(err);
      },
    );
  };

  const fetchSystemMessages = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await SystemMessageActions.GetSystemMessages(selectedCountry).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setSystemMessages(JSON.parse(JSON.stringify(res?.data)));
          setIsSearched(true);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
        }
      },
      (err) => {
        console.log(err);
      },
    );
  };

  const onClear = () => {
    setIsSearched(false);
  };

  const showErrorModal = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    refErrorMessageAlert.showAlert = false;
    setErrorAlert(refErrorMessageAlert);
  };

  useEffect(() => {
    fetchCountriesJson();
  }, []);

  useEffect(() => {
    setSystemMessageCMSData(
      candidateCMSData?.pageContent["Candidates.SystemMessage"],
    );
    setCommonComponentCMSData(candidateCMSData?.pageContent["CommonComponent"]);
  }, [candidateCMSData]);

  return (
    <React.Fragment>
      <Helmet title="View System Message" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">
          {fetchSystemMessageLabel("SystemMessage.viewSystemMessage")}
        </h1>
        <Card>
          {errorAlert?.showAlert && (
            <ErrorAlertMessage
              alertMessage="AllPages.ErrorAlertMessage"
              showAlert={showErrorModal}
            />
          )}
          <Card.Body>
            <Row>
              <Col lg="2">
                {fetchSystemMessageLabel("SystemMessage.Search.CountryLabel")}
              </Col>
              <Col lg="4" className="mb-4">
                <Select
                  className="react-select-container"
                  classNamePrefix="country-select"
                  name="country"
                  options={countriesJson}
                  onChange={(data) => {
                    setSelectedCountry(data.value);
                  }}
                  aria-label="Country Select"
                />
              </Col>
              <Col lg="2">
                <Row>
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    onClick={fetchSystemMessages}
                  >
                    {fetchCommonComponentLabel("Common.Search")}
                  </Button>
                </Row>
                <Row className="mt-3">
                  <Button
                    type="reset"
                    variant="primary"
                    size="lg"
                    onClick={onClear}
                  >
                    {fetchCommonComponentLabel("Common.Clear")}
                  </Button>
                </Row>
              </Col>
            </Row>
            {isSearched ? (
              systemMessages && systemMessages.length > 0 ? (
                <SystemMessageTable
                  systemMessageCMSData={systemMessageCMSData}
                  columns={messageTableColumn}
                  data={systemMessages}
                />
              ) : (
                <div>{fetchCommonComponentLabel("Common.NoResult")}</div>
              )
            ) : (
              <div></div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default SearchSystemMessage;
